import React, { useCallback, useEffect, useMemo, useState } from "react";
import MainLayout from "../components/mainLayout";
import { api } from "../api";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import {
  Plus,
  RefreshCw,
  Search,
  X,
  Save,
  Phone,
  Store,
  CalendarDays,
  DollarSign,
  User,
  GripVertical,
  Trophy,
  Ban,
} from "lucide-react";
import { showError } from "../utils/toast";
import "../sass/pages/leads.scss";

const COLUNAS = [
  { status: "LEAD_ENTRADA", titulo: "ETAPA DE LEADS DE ENTRADA", cor: "entrada" },
  { status: "CONTATO_INICIAL", titulo: "CONTATO INICIAL", cor: "inicial" },
  { status: "RETORNO_CONTATO", titulo: "RETORNO DE CONTATO", cor: "retorno" },
  { status: "NEGOCIACAO", titulo: "NEGOCIAÇÃO", cor: "negociacao" },
];

const CanalOpts = [
  { value: "SITE", label: "Site" },
  { value: "WHATSAPP", label: "WhatsApp" },
  { value: "PHONE", label: "Telefone" },
  { value: "INDICACAO", label: "Indicação" },
  { value: "OUTRO", label: "Outro" },
];

const emptyForm = {
  idLead: null,
  nome: "",
  contatoNome: "",
  contatoEmail: "",
  contatoTel: "",
  canal: "WHATSAPP",
  tipoComercio: "",
  jaPossuiSistema: false,
  valorEstimado: "",
  tags: "",
  descricao: "",
};

const fmtBRL = (n) =>
  Number(n || 0).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
  });

const fmtDate = (value) => {
  if (!value) return "-";
  return new Date(value).toLocaleDateString("pt-BR");
};

const onlyNumberOrEmpty = (value) => {
  if (value === "" || value === null || value === undefined) return "";
  return String(value).replace(",", ".");
};

function mapLeadToForm(lead) {
  return {
    idLead: lead.ID_LEAD,
    nome: lead.NOME || "",
    contatoNome: lead.CONTATO_NOME || "",
    contatoEmail: lead.CONTATO_EMAIL || "",
    contatoTel: lead.CONTATO_TEL || "",
    canal: lead.CANAL || "WHATSAPP",
    tipoComercio: lead.TIPO_COMERCIO || "",
    jaPossuiSistema: Boolean(lead.JA_POSSUI_SISTEMA),
    valorEstimado: lead.VALOR_ESTIMADO ?? "",
    tags: lead.TAGS || "",
    descricao: lead.DESCRICAO || "",
  };
}

export default function LeadsKanban() {
  const [q, setQ] = useState("");
  const [canal, setCanal] = useState("");
  const [kanban, setKanban] = useState({ resumo: {}, colunas: {} });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerMode, setDrawerMode] = useState("create");
  const [selectedLead, setSelectedLead] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const [dragLead, setDragLead] = useState(null);
  const [dragOverColumn, setDragOverColumn] = useState("");

  const loadKanban = useCallback(async () => {
    setLoading(true);

    try {
      const params = {};
      if (q.trim()) params.q = q.trim();
      if (canal.trim()) params.canal = canal.trim();

      const { data } = await api.get("/leads/kanban", { params });

      setKanban({
        resumo: data?.resumo || {},
        colunas: data?.colunas || {},
      });
    } catch (err) {
      showError(
        err?.response?.data?.message ||
          err?.message ||
          "Falha ao carregar Kanban de leads."
      );
      setKanban({ resumo: {}, colunas: {} });
    } finally {
      setLoading(false);
    }
  }, [q, canal]);

  useEffect(() => {
    const timer = setTimeout(loadKanban, 300);
    return () => clearTimeout(timer);
  }, [loadKanban]);

  const totais = useMemo(() => {
    const totalLeads = COLUNAS.reduce(
      (acc, col) => acc + Number(kanban?.resumo?.[col.status]?.quantidade || 0),
      0
    );

    const valorNegociacao = Number(kanban?.resumo?.NEGOCIACAO?.valorTotal || 0);

    return { totalLeads, valorNegociacao };
  }, [kanban]);

  const openCreate = () => {
    setDrawerMode("create");
    setSelectedLead(null);
    setForm(emptyForm);
    setDrawerOpen(true);
  };

  const openEdit = (lead) => {
    setDrawerMode("edit");
    setSelectedLead(lead);
    setForm(mapLeadToForm(lead));
    setDrawerOpen(true);
  };

  const closeDrawer = () => {
    if (saving) return;
    setDrawerOpen(false);
    setSelectedLead(null);
    setForm(emptyForm);
  };

  const updateForm = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const saveLead = async (e) => {
    e.preventDefault();

    if (!form.nome.trim()) return showError("Informe o nome do lead.");
    if (!form.contatoTel.trim()) return showError("Informe o celular do lead.");
    if (!form.tipoComercio.trim()) return showError("Informe o tipo de comércio.");

    setSaving(true);

    try {
      const payload = {
        nome: form.nome.trim(),
        contatoNome: form.contatoNome?.trim() || form.nome.trim(),
        contatoEmail: form.contatoEmail?.trim() || "",
        contatoTel: form.contatoTel.trim(),
        canal: form.canal || "WHATSAPP",
        tipoComercio: form.tipoComercio.trim(),
        jaPossuiSistema: Boolean(form.jaPossuiSistema),
        valorEstimado:
          form.valorEstimado === ""
            ? null
            : Number(onlyNumberOrEmpty(form.valorEstimado)),
        tags: form.tags?.trim() || "",
        descricao: form.descricao?.trim() || "",
      };

      if (drawerMode === "create") {
        await api.post("/leads", payload);
      } else {
        await api.patch(`/leads/${form.idLead}`, payload);
      }

      closeDrawer();
      await loadKanban();
    } catch (err) {
      showError(err?.response?.data?.message || err?.message || "Falha ao salvar lead.");
    } finally {
      setSaving(false);
    }
  };

  const encerrarLead = async (lead, statusFinal) => {
    const textoStatus = statusFinal === "GANHO" ? "ganho" : "perdido";

    const confirmou = window.confirm(
      `Deseja realmente marcar este lead como ${textoStatus}?\n\n${lead.NOME}`
    );

    if (!confirmou) return;

    try {
      await api.patch(`/leads/${lead.ID_LEAD}/status`, {
        status: statusFinal,
        valor: lead.VALOR_ESTIMADO ?? null,
        descricao: `Lead encerrado como ${statusFinal}.`,
        autorId: null,
      });

      if (selectedLead?.ID_LEAD === lead.ID_LEAD) {
        closeDrawer();
      }

      await loadKanban();
    } catch (err) {
      showError(
        err?.response?.data?.message ||
          err?.message ||
          "Falha ao encerrar lead."
      );
    }
  };

  const handleDragStart = (lead) => {
    setDragLead(lead);
  };

  const handleDragEnd = () => {
    setDragLead(null);
    setDragOverColumn("");
  };

  const handleDrop = async (targetStatus) => {
    if (!dragLead) return;

    const origem = dragLead.STATUS;
    const destino = targetStatus;

    setDragOverColumn("");

    if (origem === destino) {
      setDragLead(null);
      return;
    }

    try {
      await api.patch(`/leads/${dragLead.ID_LEAD}/status`, {
        status: destino,
        valor: dragLead.VALOR_ESTIMADO ?? null,
        descricao: `Lead movido de ${origem} para ${destino}.`,
        autorId: null,
      });

      await loadKanban();
    } catch (err) {
      showError(err?.response?.data?.message || err?.message || "Falha ao mover lead.");
    } finally {
      setDragLead(null);
    }
  };

  return (
    <MainLayout>
      <div className="leads-kanban-page">
        <header className="leads-kanban-header">
          <div>
            <h1>CRM de Leads</h1>
            <p>Pipeline comercial com entrada automática e gestão por Kanban.</p>
          </div>

          <div className="leads-kanban-actions">
            <button className="btn ghost" onClick={loadKanban}>
              <RefreshCw size={16} />
              Atualizar
            </button>

            <button className="btn primary" onClick={openCreate}>
              <Plus size={16} />
              Novo Lead
            </button>
          </div>
        </header>

        <section className="leads-metrics">
          <div className="metric-card">
            <span>Total no funil</span>
            <strong>{totais.totalLeads}</strong>
          </div>

          <div className="metric-card highlight">
            <span>Valor em negociação</span>
            <strong>{fmtBRL(totais.valorNegociacao)}</strong>
          </div>

          <div className="filter-card">
            <div className="input-icon">
              <Search size={16} />
              <input
                type="search"
                placeholder="Buscar lead, telefone, comércio..."
                value={q}
                onChange={(e) => setQ(e.target.value)}
              />
            </div>

            <select value={canal} onChange={(e) => setCanal(e.target.value)}>
              <option value="">Todos os canais</option>
              {CanalOpts.map((item) => (
                <option key={item.value} value={item.value}>
                  {item.label}
                </option>
              ))}
            </select>
          </div>
        </section>

        <section className="kanban-board">
          {loading
            ? COLUNAS.map((col) => (
                <div className="kanban-column" key={col.status}>
                  <Skeleton height={36} />
                  <Skeleton height={90} count={4} style={{ marginTop: 12 }} />
                </div>
              ))
            : COLUNAS.map((col) => {
                const leads = kanban?.colunas?.[col.status] || [];
                const resumo = kanban?.resumo?.[col.status] || {};
                const isOver = dragOverColumn === col.status;

                return (
                  <div
                    key={col.status}
                    className={`kanban-column ${col.cor} ${isOver ? "drag-over" : ""}`}
                    onDragOver={(e) => {
                      e.preventDefault();
                      setDragOverColumn(col.status);
                    }}
                    onDragLeave={() => setDragOverColumn("")}
                    onDrop={() => handleDrop(col.status)}
                  >
                    <div className="kanban-column-header">
                      <div>
                        <h2>{col.titulo}</h2>
                        <span>
                          {Number(resumo.quantidade || 0)} leads
                          {col.status === "NEGOCIACAO"
                            ? `: ${fmtBRL(Number(resumo.valorTotal || 0))}`
                            : ""}
                        </span>
                      </div>
                    </div>

                    <div className="kanban-cards">
                      {leads.length === 0 ? (
                        <div className="empty-column">Nenhum lead nesta etapa.</div>
                      ) : (
                        leads.map((lead) => (
                          <article
                            key={lead.ID_LEAD}
                            className="lead-card"
                            draggable
                            onDragStart={() => handleDragStart(lead)}
                            onDragEnd={handleDragEnd}
                            onClick={() => openEdit(lead)}
                          >
                            <div className="lead-card-top">
                              <div className="avatar">
                                <User size={18} />
                              </div>

                              <div className="lead-main">
                                <strong title={lead.NOME}>{lead.NOME}</strong>
                                <small>{fmtDate(lead.DT_CONTATO || lead.CREATED_AT)}</small>
                              </div>

                              <GripVertical className="drag-icon" size={16} />
                            </div>

                            <div className="lead-info">
                              <span title={lead.CONTATO_TEL || "-"}>
                                <Phone size={13} />
                                <b>{lead.CONTATO_TEL || "-"}</b>
                              </span>

                              <span title={lead.TIPO_COMERCIO || "-"}>
                                <Store size={13} />
                                <b>{lead.TIPO_COMERCIO || "-"}</b>
                              </span>

                              <span>
                                <DollarSign size={13} />
                                <b>{fmtBRL(lead.VALOR_ESTIMADO)}</b>
                              </span>
                            </div>

                            <div className="lead-tags">
                              <span>{lead.CANAL || "WHATSAPP"}</span>
                              <span>
                                {lead.JA_POSSUI_SISTEMA
                                  ? "Já possui sistema"
                                  : "Sem sistema"}
                              </span>
                            </div>

                            <div className="lead-card-actions">
                              <button
                                type="button"
                                className="lead-action won"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  encerrarLead(lead, "GANHO");
                                }}
                              >
                                <Trophy size={14} />
                                Ganho
                              </button>

                              <button
                                type="button"
                                className="lead-action lost"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  encerrarLead(lead, "PERDIDO");
                                }}
                              >
                                <Ban size={14} />
                                Perdido
                              </button>
                            </div>
                          </article>
                        ))
                      )}
                    </div>
                  </div>
                );
              })}
        </section>

        {drawerOpen && (
          <div className="lead-drawer-backdrop" onMouseDown={closeDrawer}>
            <aside className="lead-drawer" onMouseDown={(e) => e.stopPropagation()}>
              <div className="lead-drawer-header">
                <div>
                  <h2>{drawerMode === "create" ? "Novo Lead" : "Editar Lead"}</h2>
                  <p>
                    {drawerMode === "create"
                      ? "Cadastro manual para entrada no Kanban."
                      : `Lead #${selectedLead?.ID_LEAD}`}
                  </p>
                </div>

                <button className="icon-btn" onClick={closeDrawer}>
                  <X size={20} />
                </button>
              </div>

              <form className="lead-form" onSubmit={saveLead}>
                <div className="form-grid">
                  <label>
                    Nome *
                    <input
                      value={form.nome}
                      onChange={(e) => updateForm("nome", e.target.value)}
                      placeholder="Nome do lead"
                    />
                  </label>

                  <label>
                    Celular *
                    <input
                      value={form.contatoTel}
                      onChange={(e) => updateForm("contatoTel", e.target.value)}
                      placeholder="11999999999"
                    />
                  </label>

                  <label>
                    Nome do contato
                    <input
                      value={form.contatoNome}
                      onChange={(e) => updateForm("contatoNome", e.target.value)}
                      placeholder="Responsável pelo contato"
                    />
                  </label>

                  <label>
                    E-mail
                    <input
                      value={form.contatoEmail}
                      onChange={(e) => updateForm("contatoEmail", e.target.value)}
                      placeholder="email@dominio.com"
                    />
                  </label>

                  <label>
                    Tipo de comércio *
                    <input
                      value={form.tipoComercio}
                      onChange={(e) => updateForm("tipoComercio", e.target.value)}
                      placeholder="Mercado, ótica, restaurante..."
                    />
                  </label>

                  <label>
                    Canal
                    <select
                      value={form.canal}
                      onChange={(e) => updateForm("canal", e.target.value)}
                    >
                      {CanalOpts.map((item) => (
                        <option key={item.value} value={item.value}>
                          {item.label}
                        </option>
                      ))}
                    </select>
                  </label>

                  <label>
                    Valor estimado
                    <input
                      value={form.valorEstimado}
                      onChange={(e) => updateForm("valorEstimado", e.target.value)}
                      placeholder="0,00"
                    />
                  </label>

                  <label className="switch-row">
                    <span>Já possui sistema?</span>
                    <input
                      type="checkbox"
                      checked={form.jaPossuiSistema}
                      onChange={(e) =>
                        updateForm("jaPossuiSistema", e.target.checked)
                      }
                    />
                  </label>
                </div>

                <label>
                  Tags
                  <input
                    value={form.tags}
                    onChange={(e) => updateForm("tags", e.target.value)}
                    placeholder="whatsapp, campanha, tráfego pago"
                  />
                </label>

                <label>
                  Descrição
                  <textarea
                    value={form.descricao}
                    onChange={(e) => updateForm("descricao", e.target.value)}
                    placeholder="Observações comerciais do lead..."
                    rows={5}
                  />
                </label>

                {drawerMode === "edit" && selectedLead && (
                  <div className="lead-readonly">
                    <div>
                      <CalendarDays size={15} />
                      Criado em {fmtDate(selectedLead.CREATED_AT)}
                    </div>
                    <div>Status atual: {selectedLead.STATUS}</div>
                  </div>
                )}

                {drawerMode === "edit" && selectedLead && (
                  <div className="lead-close-actions">
                    <button
                      type="button"
                      className="btn success"
                      onClick={() => encerrarLead(selectedLead, "GANHO")}
                      disabled={saving}
                    >
                      <Trophy size={16} />
                      Marcar como ganho
                    </button>

                    <button
                      type="button"
                      className="btn danger"
                      onClick={() => encerrarLead(selectedLead, "PERDIDO")}
                      disabled={saving}
                    >
                      <Ban size={16} />
                      Marcar como perdido
                    </button>
                  </div>
                )}

                <div className="drawer-footer">
                  <button
                    type="button"
                    className="btn ghost"
                    onClick={closeDrawer}
                    disabled={saving}
                  >
                    Cancelar
                  </button>

                  <button type="submit" className="btn primary" disabled={saving}>
                    <Save size={16} />
                    {saving ? "Salvando..." : "Salvar"}
                  </button>
                </div>
              </form>
            </aside>
          </div>
        )}
      </div>
    </MainLayout>
  );
}